<template>

    <div v-if="showModal" @click.self="hide"
         class="fixed top-0 left-0 h-full w-full z-50 flex flex-column items-center justify-center"
         style="background-color: rgba(0,0,0,.2);">
        <div class="w-full sm:w-1/3 lg:w-1/3 bg-white rounded-lg mx-6" style="max-height: 100%;">

            <div class="rg-modal-header">
                <span>Rate Lead</span>

                <a href="javascript: void(0);" class="rg-modal-close-btn" @click="hide"><i class="mdi mdi-close"></i></a>
            </div>

            <div class="p-6" style="height: calc(100% - 110px); overflow: auto;">

                <div class="flex flex-wrap">
                    <div class="w-full pr-5 mb-1 md:mb-0">
                        <div class="lead-feedback">
                            <!-- Already Rated -->
                            <div v-if="lead.rated_at">
                                <div class="bg-green-50 border border-green-200 rounded-xl p-4 text-green-800">
                                    <h3 class="font-semibold mb-1">Lead has already been rated</h3>
                                    <p><strong>Satisfaction:</strong> {{ satisfactionLabel(lead.satisfaction) }}</p>
                                    <p v-if="lead.reason"><strong>Reason:</strong> {{ reasonLabel(lead.reason) }}</p>
                                    <p v-if="lead.additional_comments"><strong>Comments:</strong> {{ lead.additional_comments }}</p>
                                    <p class="text-xs text-gray-500 mt-1">Rated on {{ formatDate(lead.rated_at) }}</p>
                                </div>
                                </div>

                                <!-- Rating Form -->
                                <div v-else>
                                <h2 class="text-lg font-medium mb-4">How satisfied are you with the quality of this lead?</h2>
                                <div v-show="!selectedSatisfaction" class="space-y-3">
                                    <div
                                    v-for="option in satisfactionOptions"
                                    :key="option.value"
                                    @click="selectSatisfaction(option.value)"
                                    class="cursor-pointer flex items-center space-x-3 p-3 border rounded-xl hover:bg-gray-100 mb-2"
                                    :class="{ 'border-blue-500 bg-blue-50': selectedSatisfaction === option.value }"
                                    >
                                    <span class="text-2xl">{{ option.emoji }}</span>
                                    <span>{{ option.label }}</span>
                                    </div>
                                </div>

                                <div v-if="selectedSatisfaction" class="mt-6">
                                    <h3 class="font-medium mb-3">
                                    {{ isSatisfied ? 'Why are you satisfied?' : 'Why are you not satisfied?' }}
                                    </h3>
                                    <div class="space-y-3">
                                    <div
                                        v-for="reason in filteredReasons"
                                        :key="reason.value"
                                        @click="selectReason(reason.value)"
                                        class="cursor-pointer flex items-center space-x-2 p-3 border rounded-xl hover:bg-gray-100 mb-2 transition duration-200"
                                        :class="{
                                            'border-blue-500 bg-blue-100 font-semibold shadow-sm scale-[1.01]': selectedReason === reason.value,
                                            'border-gray-300': selectedReason !== reason.value
                                        }"
                                        >
                                        <span>{{ reason.label }}</span>
                                    </div>

                                    <div v-if="selectedReason === 'OTHER_SATISFIED_REASON' || selectedReason === 'OTHER_DISSATISFIED_REASON'" class="mt-2">
                                        <textarea
                                        v-model="additionalComments"
                                        rows="3"
                                        class="w-full p-2 border rounded-md"
                                        placeholder="Please provide details (no personal info)"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            <button
                                :disabled="!canSubmit"
                                @click="submitFeedback"
                                class="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                Submit
                            </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="bg-gray-100 rounded-b-lg px-6 py-4">

                <button class="rounded text-blue text-xs text-bold border border-blue focus:outline-none py-2 px-4 mr-2 float-right"
                        @click="hide">
                    CANCEL
                </button>

                <div class="clearfix"></div>

            </div>


        </div>
    </div>

</template>


<script>
import axios from 'axios'

export default {

    data: function () {
        return {
            showModal: false,
            saving: false,
            success: null,
            error: null,
            lead: null,

            selectedSatisfaction: null,
            selectedReason: null,
            additionalComments: ''
        }
    },

    computed: {
        satisfactionOptions() {
            return [
                { emoji: '😃', value: 'VERY_SATISFIED', label: 'Very satisfied.' },
                { emoji: '🙂', value: 'SATISFIED', label: 'Somewhat satisfied.' },
                { emoji: '😐', value: 'NEUTRAL', label: 'Neither satisfied nor dissatisfied.' },
                { emoji: '🙁', value: 'DISSATISFIED', label: 'Somewhat dissatisfied.' },
                { emoji: '☹️', value: 'VERY_DISSATISFIED', label: 'Very dissatisfied.' }
            ]
        },

        satisfiedReasons() {
            return [
                { value: 'BOOKED_CUSTOMER', label: 'It converted into a booked customer or client' },
                { value: 'LIKELY_BOOKED_CUSTOMER', label: 'It could convert into a booked customer or client soon' },
                { value: 'SERVICE_RELATED', label: 'It is relevant to the services the business provides' },
                { value: 'HIGH_VALUE_SERVICE', label: 'It is for a service that generates high value for the business' },
                { value: 'OTHER_SATISFIED_REASON', label: 'Other (please specify)' }
            ]
        },
        unsatisfiedReasons() {
            return [
                { value: 'GEO_MISMATCH', label: 'It is not located in the service area(s) for the business' },
                { value: 'JOB_TYPE_MISMATCH', label: 'It is for a service the business does not provide' },
                { value: 'NOT_READY_TO_BOOK', label: 'The person calling was not ready to book services' },
                { value: 'SPAM', label: 'It is spam (e.g., robocall, silent caller, scam)' },
                { value: 'DUPLICATE', label: 'It is a duplicate lead' },
                { value: 'SOLICITATION', label: 'It is a person seeking employment or trying to sell something' },
                { value: 'OTHER_DISSATISFIED_REASON', label: 'Other (please specify)' }
            ];
        },
        filteredReasons() {
            return ['VERY_SATISFIED', 'SATISFIED'].includes(this.selectedSatisfaction)
                ? this.satisfiedReasons
                : this.unsatisfiedReasons
        },
        isSatisfied() {
            return ['VERY_SATISFIED', 'SATISFIED'].includes(this.selectedSatisfaction)
        },
        canSubmit() {
            return (
                this.selectedSatisfaction &&
                this.selectedReason &&
                (this.selectedReason !== 'OTHER' || this.additionalComments.trim().length > 0)
            )
        }
    },

    methods: {
        show() {
            this.showModal = true
        },
        hide() {
            this.showModal = false
            this.saving = false
        },

        selectSatisfaction(value) {
            console.log('value', value);
            this.selectedSatisfaction = value
            this.selectedReason = null
            this.additionalComments = ''

            if (value == 'NEUTRAL')
                this.submitFeedback();
        },
        selectReason(value) {
            this.selectedReason = value
        },
        async submitFeedback() {
            // try {
                const payload = {
                    lead_id: this.lead.id,
                    satisfaction: this.selectedSatisfaction,
                    reason: this.selectedReason,
                    comments: this.additionalComments
                }

                this.lead.rated_at = new Date();
                this.lead.reason = this.selectedReason;
                this.lead.satisfaction = this.selectedSatisfaction;
                this.lead.additional_comments = this.additionalComments

                let response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/call-tracking/rate-lead`, payload, {
                    headers: {
                        'Authorization': 'Bearer ' + this.$store.state.auth.jwt
                    }
                });

                this.$parent.$parent.getMessages(true);
            // } catch (error) {
            //     console.error('Feedback submission failed:', error)
            //     alert('Error submitting feedback.')
            // }
        },
        satisfactionLabel(value) {
            const map = this.satisfactionOptions.find(o => o.value === value)
            return map ? map.label : value
        },
        reasonLabel(value) {
            const all = [...this.satisfiedReasons, ...this.unsatisfiedReasons]
            const map = all.find(r => r.value === value)
            return map ? map.label : value
        },
        formatDate(datetime) {
            const date = new Date(datetime)
            return date.toLocaleString()
        }
    },

}
</script>


<style scoped>
    .transition {
    transition: all 0.2s ease;
    }
</style>